import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  ArrowLeft, Plus, Pencil, Trash2, FileDown, RefreshCw, Wallet, Repeat,
  Receipt, BedDouble, Wrench, AlertCircle, Sparkles, Camera, Loader2, Image as ImageIcon,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, CartesianGrid, XAxis, YAxis, Tooltip, Bar,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import { toast } from "sonner";
import { todayLocalIso } from "@/lib/dateUtils";

const PAYMENT_METHODS = [
  { k: "bank_transfer", label: "Bank transfer" },
  { k: "mpesa",         label: "M-Pesa" },
  { k: "cash",          label: "Cash" },
  { k: "card",          label: "Card" },
  { k: "cheque",        label: "Cheque" },
];

const fmtKES = (v) => `KES ${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const todayIso = () => todayLocalIso();
const monthStart = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`; };

const blankDraft = (catId) => ({
  id: null,
  date: todayIso(),
  category_id: catId || "",
  amount: 0,
  currency: "KES",
  vendor: "",
  description: "",
  payment_method: "bank_transfer",
  payment_reference: "",
  unit_id: "__none__",
  notes: "",
  is_capex: false,
  asset_name: "",
  asset_serial: "",
  purchase_date: "",
  useful_life_months: "",
  depreciation_method: "straight_line",
  salvage_value: 0,
  receipt_image_url: null,
});

export default function Expenses() {
  const { user, hasPerm } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("list");  // list | summary
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(todayIso());
  const [categories, setCategories] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [units, setUnits] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterCategory, setFilterCategory] = useState("__all__");
  const [filterKind, setFilterKind] = useState("__all__");
  const [draft, setDraft] = useState(null);
  const [tmplDraft, setTmplDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);  // {confidence, ...} for the banner

  // Phase 42: gate write actions on the actual `expenses.manage` permission
  // so booking_coordinator + any custom role admins create can also use the
  // New expense / Scan receipt buttons.
  const canWrite = hasPerm("expenses.manage");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cats, tmpls, us, ex, sm] = await Promise.all([
        api.get("/housing/expense-categories"),
        api.get("/housing/expense-templates"),
        api.get("/housing/units"),
        api.get(`/housing/expenses?from_date=${from}&to_date=${to}`),
        api.get(`/housing/expenses/summary?from_date=${from}&to_date=${to}`),
      ]);
      setCategories(cats.data);
      setTemplates(tmpls.data);
      setUnits(us.data);
      setExpenses(ex.data);
      setSummary(sm.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load");
    } finally { setLoading(false); }
  }, [from, to]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    return expenses.filter((e) => {
      if (filterCategory !== "__all__" && e.category_id !== filterCategory) return false;
      if (filterKind !== "__all__" && e.category_kind !== filterKind) return false;
      return true;
    });
  }, [expenses, filterCategory, filterKind]);

  const catById = useMemo(() => Object.fromEntries(categories.map((c) => [c.id, c])), [categories]);

  const openNew = () => setDraft(blankDraft(categories[0]?.id));
  const openEdit = (e) => setDraft({
    ...blankDraft(),
    ...e,
    unit_id: e.unit_id || "__none__",
    useful_life_months: e.useful_life_months || "",
  });

  const fromTemplate = (tmpl) => {
    setDraft({
      ...blankDraft(tmpl.category_id),
      category_id: tmpl.category_id,
      amount: tmpl.default_amount,
      currency: tmpl.default_currency,
      vendor: tmpl.vendor || "",
      description: tmpl.notes || tmpl.label,
      payment_method: tmpl.payment_method || "bank_transfer",
      template_id: tmpl.id,
    });
  };

  const scanReceipt = async (file) => {
    setScanning(true);
    setScanResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/housing/receipts/scan", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      // Pre-fill the New Expense dialog from the OCR result
      setDraft({
        ...blankDraft(data.category_id || categories[0]?.id),
        date: data.date || todayIso(),
        category_id: data.category_id || categories[0]?.id || "",
        amount: data.amount || 0,
        currency: data.currency || "KES",
        vendor: data.vendor || "",
        description: data.description || "",
        payment_method: data.payment_method || "bank_transfer",
        payment_reference: data.payment_reference || "",
        notes: data.notes || "",
        receipt_image_url: data.receipt_image_url || null,
      });
      setScanResult({
        confidence: data.confidence || 0,
        line_items: data.line_items || [],
        receipt_image_url: data.receipt_image_url,
      });
      const pct = Math.round((data.confidence || 0) * 100);
      if (pct >= 80) toast.success(`Scanned with ${pct}% confidence — review and save`);
      else if (pct >= 40) toast.warning(`Scanned with ${pct}% confidence — please double-check the fields`);
      else toast.error(`Low confidence (${pct}%) — receipt may be unreadable. Edit fields manually.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Receipt scan failed");
    } finally {
      setScanning(false);
    }
  };

  const save = async () => {
    if (!draft.category_id) { toast.error("Pick a category"); return; }
    if (Number(draft.amount) <= 0) { toast.error("Amount must be > 0"); return; }
    setSaving(true);
    const body = {
      ...draft,
      amount: Number(draft.amount),
      unit_id: draft.unit_id === "__none__" ? null : draft.unit_id,
      useful_life_months: draft.useful_life_months ? Number(draft.useful_life_months) : null,
      salvage_value: Number(draft.salvage_value || 0),
    };
    delete body.id;
    delete body.category_name; delete body.category_kind; delete body.category_color;
    try {
      if (draft.id) {
        await api.put(`/housing/expenses/${draft.id}`, body);
        toast.success("Expense updated");
      } else {
        await api.post("/housing/expenses", body);
        toast.success("Expense recorded");
      }
      setDraft(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const del = async (e) => {
    if (!window.confirm(`Delete ${e.category_name} expense of ${e.currency} ${e.amount}?`)) return;
    try { await api.delete(`/housing/expenses/${e.id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };

  const runRecurring = async () => {
    if (!window.confirm("Generate this month's recurring expenses from active templates?")) return;
    try {
      const { data } = await api.post(`/housing/expenses/auto-generate-recurring?period=${from.slice(0,7)}`);
      toast.success(`${data.created} created, ${data.skipped} already existed`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const exportFile = async (format) => {
    try {
      const res = await api.get(`/housing/expenses/export?from_date=${from}&to_date=${to}&format=${format}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `expenses-${todayIso()}.${format === "xlsx" ? "xlsx" : "csv"}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export failed"); }
  };

  // Template CRUD
  const saveTemplate = async () => {
    if (!tmplDraft.label?.trim()) { toast.error("Label required"); return; }
    if (!tmplDraft.category_id) { toast.error("Category required"); return; }
    try {
      const body = { ...tmplDraft, default_amount: Number(tmplDraft.default_amount || 0), day_of_month: Number(tmplDraft.day_of_month || 1) };
      delete body.id;
      if (tmplDraft.id) await api.put(`/housing/expense-templates/${tmplDraft.id}`, body);
      else await api.post("/housing/expense-templates", body);
      toast.success("Template saved");
      setTmplDraft(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const deleteTemplate = async (t) => {
    if (!window.confirm(`Delete template "${t.label}"?`)) return;
    try { await api.delete(`/housing/expense-templates/${t.id}`); load(); }
    catch (e) { toast.error("Delete failed"); }
  };

  if (loading || !summary) return <div className="p-6 text-[#7A8A84]">Loading expenses…</div>;

  const t = summary.totals;

  return (
    <div className="space-y-6 fade-in" data-testid="expenses-page">
      <Button variant="ghost" onClick={() => navigate(-1)} className="text-[#525C58]" data-testid="back-btn">
        <ArrowLeft className="h-4 w-4 mr-2" /> Back
      </Button>

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-4xl font-semibold tracking-tight flex items-center gap-3" style={{ fontFamily: "Manrope, sans-serif" }}>
            <Wallet className="h-8 w-8 text-[#B5392F]" /> Expenses
          </h1>
          <p className="text-[#525C58] mt-1">Operating costs, payroll, repairs and CAPEX — feeds into the Forecast and Audit log.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-40" data-testid="from-date" />
          <span className="text-[#7A8A84]">→</span>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-40" data-testid="to-date" />
          <Button variant="outline" size="sm" onClick={load} data-testid="refresh-btn"><RefreshCw className="h-3.5 w-3.5" /></Button>
          <Button variant="outline" size="sm" onClick={() => exportFile("csv")} className="border-[#1B4332]/20" data-testid="export-csv"><FileDown className="h-3.5 w-3.5 mr-1" /> CSV</Button>
          <Button variant="outline" size="sm" onClick={() => exportFile("xlsx")} className="border-[#1B4332]/20" data-testid="export-xlsx"><FileDown className="h-3.5 w-3.5 mr-1" /> Excel</Button>
        </div>
      </div>

      {/* Top stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total in window" value={fmtKES(t.total_KES_equiv)} sub={`${t.expense_count} entries`} color="#B5392F" testId="stat-total" />
        <StatCard label="Operating" value={fmtKES(t.operating_KES)} sub="day-to-day overhead" color="#0F4C81" testId="stat-operating" />
        <StatCard label="Payroll" value={fmtKES(t.payroll_KES)} sub="staff & house help" color="#1B4332" testId="stat-payroll" />
        <StatCard label="CAPEX" value={fmtKES(t.capex_KES)} sub="assets / equipment" color="#40916C" testId="stat-capex" />
      </div>

      {/* Tab buttons */}
      <div className="flex items-center gap-1 border-b border-[#E8E8E6]">
        <TabButton current={tab} setTab={setTab} k="list" label={`Entries (${filtered.length})`} />
        <TabButton current={tab} setTab={setTab} k="summary" label="Charts" />
        <TabButton current={tab} setTab={setTab} k="templates" label={`Recurring (${templates.length})`} />
      </div>

      {tab === "list" && (
        <Card className="border-[#E8E8E6]">
          <CardHeader>
            <CardTitle className="text-base flex items-center justify-between gap-3 flex-wrap">
              <span className="flex items-center gap-2"><Receipt className="h-4 w-4" /> Expense entries</span>
              <div className="flex items-center gap-2 flex-wrap">
                <Select value={filterKind} onValueChange={setFilterKind}>
                  <SelectTrigger className="w-32 text-sm" data-testid="filter-kind"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All kinds</SelectItem>
                    <SelectItem value="operating">Operating</SelectItem>
                    <SelectItem value="payroll">Payroll</SelectItem>
                    <SelectItem value="capex">CAPEX</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filterCategory} onValueChange={setFilterCategory}>
                  <SelectTrigger className="w-44 text-sm" data-testid="filter-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All categories</SelectItem>
                    {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {canWrite && (
                  <>
                    <Button size="sm" onClick={openNew} className="bg-[#B5392F] hover:bg-[#8E2A23]" data-testid="new-expense-btn">
                      <Plus className="h-3.5 w-3.5 mr-1" /> New expense
                    </Button>
                    <label className="inline-flex items-center text-sm font-medium px-3 h-9 rounded-md cursor-pointer transition bg-[#1B4332] text-white hover:bg-[#143526] disabled:opacity-50" data-testid="scan-receipt-label">
                      {scanning ? (
                        <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Scanning…</>
                      ) : (
                        <><Camera className="h-3.5 w-3.5 mr-1" /> Scan receipt</>
                      )}
                      <input
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif"
                        capture="environment"
                        className="hidden"
                        disabled={scanning}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) scanReceipt(f);
                          e.target.value = "";  // allow re-picking the same file
                        }}
                        data-testid="scan-receipt-input"
                      />
                    </label>
                    <Button size="sm" variant="outline" onClick={runRecurring} className="border-purple-200 text-purple-700" data-testid="run-recurring-btn">
                      <Repeat className="h-3.5 w-3.5 mr-1" /> Run recurring
                    </Button>
                  </>
                )}
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {filtered.length === 0 ? (
              <p className="text-center py-10 text-[#7A8A84]">No expenses in this window. {canWrite && <Button variant="ghost" onClick={openNew} className="text-[#1B4332]">Add the first one →</Button>}</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Vendor / description</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Method</TableHead>
                    <TableHead>By</TableHead>
                    <TableHead />
                  </TableRow></TableHeader>
                  <TableBody>
                    {filtered.map((e) => (
                      <TableRow key={e.id} data-testid={`expense-row-${e.id}`}>
                        <TableCell className="text-sm">{e.date}</TableCell>
                        <TableCell>
                          <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full font-medium border"
                                style={{ borderColor: e.category_color || "#E8E8E6", color: e.category_color || "#525C58", backgroundColor: `${e.category_color || "#525C58"}11` }}>
                            {e.category_kind === "capex" && <Sparkles className="h-3 w-3" />}
                            {e.category_kind === "payroll" && <Wallet className="h-3 w-3" />}
                            {e.category_name}
                          </span>
                          {e.is_capex && e.asset_name && <p className="text-[10px] text-[#7A8A84] mt-1">Asset: {e.asset_name}</p>}
                        </TableCell>
                        <TableCell>
                          <p className="font-medium">{e.vendor || <span className="text-[#A3ACA8]">—</span>}</p>
                          <p className="text-[11px] text-[#7A8A84] line-clamp-1">{e.description}</p>
                        </TableCell>
                        <TableCell className="text-right tabular-nums font-semibold">{e.currency} {Number(e.amount).toLocaleString()}</TableCell>
                        <TableCell className="text-xs text-[#525C58]">{(PAYMENT_METHODS.find((m) => m.k === e.payment_method) || {}).label || e.payment_method}{e.payment_reference && <div className="text-[10px] text-[#A3ACA8]">{e.payment_reference}</div>}</TableCell>
                        <TableCell className="text-xs text-[#525C58]">{e.recorded_by_name}{e.auto_generated && <Badge className="ml-1 bg-purple-50 text-purple-700 text-[9px]">AUTO</Badge>}</TableCell>
                        <TableCell className="text-right space-x-1 whitespace-nowrap">
                          {canWrite && (
                            <Button size="sm" variant="ghost" onClick={() => openEdit(e)} className="text-[#1B4332]" data-testid={`edit-${e.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                          )}
                          {["admin","global_admin"].includes(user?.role) && (
                            <Button size="sm" variant="ghost" onClick={() => del(e)} className="text-red-600" data-testid={`delete-${e.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {tab === "summary" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="border-[#E8E8E6]" data-testid="by-category-card">
            <CardHeader><CardTitle className="text-base">By category</CardTitle></CardHeader>
            <CardContent>
              {summary.by_category.length === 0 ? <p className="text-sm text-[#7A8A84] text-center py-8">No data</p> : (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie data={summary.by_category} dataKey="amount_KES" nameKey="name" outerRadius={100} label={(d) => `${d.name}`}>
                      {summary.by_category.map((entry, i) => <Cell key={i} fill={entry.color || "#525C58"} />)}
                    </Pie>
                    <Tooltip formatter={(v) => fmtKES(v)} />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
          <Card className="border-[#E8E8E6]" data-testid="trend-card">
            <CardHeader><CardTitle className="text-base">Monthly trend</CardTitle></CardHeader>
            <CardContent>
              {summary.trend.length === 0 ? <p className="text-sm text-[#7A8A84] text-center py-8">No data</p> : (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={summary.trend}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="period" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip formatter={(v) => fmtKES(v)} />
                    <Bar dataKey="amount_KES" fill="#B5392F" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {tab === "templates" && (
        <Card className="border-[#E8E8E6]">
          <CardHeader>
            <CardTitle className="text-base flex items-center justify-between flex-wrap gap-2">
              <span className="flex items-center gap-2"><Repeat className="h-4 w-4" /> Recurring expense templates</span>
              {canWrite && (
                <Button size="sm" onClick={() => setTmplDraft({ label: "", category_id: categories[0]?.id || "", default_amount: 0, default_currency: "KES", vendor: "", recurrence: "monthly", auto_generate: true, day_of_month: 1, active: true, payment_method: "bank_transfer" })} className="bg-[#1B4332] hover:bg-[#2D6A4F]" data-testid="new-template-btn">
                  <Plus className="h-3.5 w-3.5 mr-1" /> New template
                </Button>
              )}
            </CardTitle>
            <CardDescription className="text-xs">Templates produce recurring expense entries. Click "Run recurring" on the Entries tab to generate the current month.</CardDescription>
          </CardHeader>
          <CardContent>
            {templates.length === 0 ? <p className="text-center py-8 text-[#7A8A84]">No templates yet — create one to track utilities, salaries, insurance etc.</p> : (
              <Table>
                <TableHeader><TableRow><TableHead>Label</TableHead><TableHead>Category</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Vendor</TableHead><TableHead>Auto</TableHead><TableHead /></TableRow></TableHeader>
                <TableBody>
                  {templates.map((tmpl) => (
                    <TableRow key={tmpl.id} data-testid={`template-row-${tmpl.id}`}>
                      <TableCell className="font-medium">{tmpl.label}</TableCell>
                      <TableCell className="text-sm">{catById[tmpl.category_id]?.name || "—"}</TableCell>
                      <TableCell className="text-right tabular-nums">{tmpl.default_currency} {Number(tmpl.default_amount).toLocaleString()}</TableCell>
                      <TableCell className="text-sm">{tmpl.vendor || "—"}</TableCell>
                      <TableCell>{tmpl.auto_generate ? <Badge className="bg-purple-50 text-purple-700 border border-purple-200 text-[10px]">Day {tmpl.day_of_month}</Badge> : <span className="text-[11px] text-[#7A8A84]">manual</span>}</TableCell>
                      <TableCell className="text-right space-x-1 whitespace-nowrap">
                        {canWrite && <Button size="sm" variant="ghost" onClick={() => fromTemplate(tmpl)} className="text-[#B5392F]" title="Use template now" data-testid={`use-${tmpl.id}`}><Plus className="h-3.5 w-3.5" /></Button>}
                        {canWrite && <Button size="sm" variant="ghost" onClick={() => setTmplDraft({ ...tmpl })} className="text-[#1B4332]" data-testid={`edit-tmpl-${tmpl.id}`}><Pencil className="h-3.5 w-3.5" /></Button>}
                        {canWrite && <Button size="sm" variant="ghost" onClick={() => deleteTemplate(tmpl)} className="text-red-600" data-testid={`delete-tmpl-${tmpl.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Expense dialog */}
      <Dialog open={!!draft} onOpenChange={(o) => { if (!o) { setDraft(null); setScanResult(null); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="expense-dialog">
          <DialogHeader>
            <DialogTitle>{draft?.id ? "Edit expense" : "Record expense"}</DialogTitle>
            <DialogDescription>All amounts are stored at face value; the dashboard rolls USD into KES at the current rate.</DialogDescription>
          </DialogHeader>
          {draft && (
            <div className="space-y-3">
              {scanResult && draft.receipt_image_url && (
                <div className="flex gap-3 p-3 rounded-lg border border-emerald-200 bg-emerald-50" data-testid="receipt-banner">
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}${draft.receipt_image_url}`}
                    alt="Receipt"
                    className="h-24 w-24 object-cover rounded border border-emerald-200"
                    data-testid="receipt-thumbnail"
                  />
                  <div className="flex-1 text-xs text-[#1B4332] space-y-1">
                    <div className="flex items-center gap-2 font-semibold">
                      <Sparkles className="h-3.5 w-3.5" />
                      Pre-filled from photo
                      <Badge variant="outline" className={`text-[10px] ${
                        scanResult.confidence >= 0.8 ? "bg-emerald-100 text-emerald-800 border-emerald-300"
                        : scanResult.confidence >= 0.4 ? "bg-amber-100 text-amber-800 border-amber-300"
                        : "bg-red-100 text-red-800 border-red-300"
                      }`}>
                        {Math.round((scanResult.confidence || 0) * 100)}% confidence
                      </Badge>
                    </div>
                    <p>Review the fields below — Gemini extracted them from the photo. Edit anything that's wrong before saving.</p>
                    {scanResult.line_items?.length > 0 && (
                      <details className="cursor-pointer">
                        <summary className="text-[#525C58] hover:text-[#1B4332]">
                          {scanResult.line_items.length} line items detected
                        </summary>
                        <ul className="mt-1 ml-4 list-disc text-[#525C58]">
                          {scanResult.line_items.slice(0, 8).map((li, i) => (
                            <li key={i}>{li.label} — {li.amount}</li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </div>
                </div>
              )}
              {draft.receipt_image_url && !scanResult && (
                <div className="flex gap-2 p-2 rounded border border-[#E8E8E6] bg-[#F4F4F1]" data-testid="receipt-attached">
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}${draft.receipt_image_url}`}
                    alt="Receipt"
                    className="h-16 w-16 object-cover rounded"
                  />
                  <div className="text-xs text-[#525C58] flex items-center gap-1">
                    <ImageIcon className="h-3.5 w-3.5" /> Receipt photo attached
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label>Date *</Label><Input type="date" value={draft.date} onChange={(e) => setDraft({...draft, date: e.target.value})} data-testid="dlg-date" /></div>
                <div className="space-y-1"><Label>Category *</Label>
                  <Select value={draft.category_id} onValueChange={(v) => setDraft({...draft, category_id: v})}>
                    <SelectTrigger data-testid="dlg-category"><SelectValue /></SelectTrigger>
                    <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1 col-span-2"><Label>Amount *</Label><Input type="number" min={0} step="0.01" value={draft.amount} onChange={(e) => setDraft({...draft, amount: e.target.value})} data-testid="dlg-amount" /></div>
                <div className="space-y-1"><Label>Currency</Label>
                  <Select value={draft.currency} onValueChange={(v) => setDraft({...draft, currency: v})}>
                    <SelectTrigger data-testid="dlg-currency"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="KES">KES</SelectItem><SelectItem value="USD">USD</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label>Vendor / payee</Label><Input value={draft.vendor} onChange={(e) => setDraft({...draft, vendor: e.target.value})} placeholder="e.g. KPLC, Shell" data-testid="dlg-vendor" /></div>
                <div className="space-y-1"><Label>Payment method</Label>
                  <Select value={draft.payment_method} onValueChange={(v) => setDraft({...draft, payment_method: v})}>
                    <SelectTrigger data-testid="dlg-method"><SelectValue /></SelectTrigger>
                    <SelectContent>{PAYMENT_METHODS.map((m) => <SelectItem key={m.k} value={m.k}>{m.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1"><Label>Description</Label><Textarea rows={2} value={draft.description} onChange={(e) => setDraft({...draft, description: e.target.value})} data-testid="dlg-description" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label>Reference (M-Pesa / cheque #)</Label><Input value={draft.payment_reference} onChange={(e) => setDraft({...draft, payment_reference: e.target.value})} data-testid="dlg-reference" /></div>
                <div className="space-y-1"><Label>Linked unit (optional)</Label>
                  <Select value={draft.unit_id} onValueChange={(v) => setDraft({...draft, unit_id: v})}>
                    <SelectTrigger data-testid="dlg-unit"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— Not unit-specific —</SelectItem>
                      {units.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* CAPEX block */}
              <label className="flex items-start gap-2 cursor-pointer pt-2 border-t border-[#E8E8E6]">
                <input type="checkbox" checked={!!draft.is_capex} onChange={(e) => setDraft({...draft, is_capex: e.target.checked})} className="mt-1" data-testid="dlg-is-capex" />
                <div>
                  <p className="text-sm font-medium flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5 text-[#40916C]" /> This is a CAPEX / asset purchase</p>
                  <p className="text-[11px] text-[#7A8A84]">Captures the metadata needed for asset tracking & depreciation.</p>
                </div>
              </label>
              {draft.is_capex && (
                <div className="grid grid-cols-2 gap-3 pl-6 pt-1" data-testid="capex-block">
                  <div className="space-y-1"><Label className="text-xs">Asset name</Label><Input value={draft.asset_name} onChange={(e) => setDraft({...draft, asset_name: e.target.value})} placeholder="e.g. Generator 5kVA" data-testid="dlg-asset-name" /></div>
                  <div className="space-y-1"><Label className="text-xs">Serial #</Label><Input value={draft.asset_serial} onChange={(e) => setDraft({...draft, asset_serial: e.target.value})} data-testid="dlg-asset-serial" /></div>
                  <div className="space-y-1"><Label className="text-xs">Purchase date</Label><Input type="date" value={draft.purchase_date || ""} onChange={(e) => setDraft({...draft, purchase_date: e.target.value})} data-testid="dlg-purchase-date" /></div>
                  <div className="space-y-1"><Label className="text-xs">Useful life (months)</Label><Input type="number" min={0} value={draft.useful_life_months} onChange={(e) => setDraft({...draft, useful_life_months: e.target.value})} placeholder="60" data-testid="dlg-useful-life" /></div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDraft(null)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#B5392F] hover:bg-[#8E2A23]" data-testid="dlg-save">{saving ? "Saving…" : "Save expense"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template dialog */}
      <Dialog open={!!tmplDraft} onOpenChange={(o) => !o && setTmplDraft(null)}>
        <DialogContent className="max-w-lg" data-testid="template-dialog">
          <DialogHeader><DialogTitle>{tmplDraft?.id ? "Edit template" : "New recurring template"}</DialogTitle><DialogDescription>Auto-creates an expense each month so the project manager just has to confirm.</DialogDescription></DialogHeader>
          {tmplDraft && (
            <div className="space-y-3">
              <div className="space-y-1"><Label>Label *</Label><Input value={tmplDraft.label} onChange={(e) => setTmplDraft({...tmplDraft, label: e.target.value})} placeholder="e.g. Electricity" data-testid="tmpl-label" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label>Category</Label>
                  <Select value={tmplDraft.category_id} onValueChange={(v) => setTmplDraft({...tmplDraft, category_id: v})}>
                    <SelectTrigger data-testid="tmpl-category"><SelectValue /></SelectTrigger>
                    <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1"><Label>Day of month</Label><Input type="number" min={1} max={28} value={tmplDraft.day_of_month} onChange={(e) => setTmplDraft({...tmplDraft, day_of_month: e.target.value})} data-testid="tmpl-day" /></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1 col-span-2"><Label>Default amount</Label><Input type="number" min={0} value={tmplDraft.default_amount} onChange={(e) => setTmplDraft({...tmplDraft, default_amount: e.target.value})} data-testid="tmpl-amount" /></div>
                <div className="space-y-1"><Label>Currency</Label>
                  <Select value={tmplDraft.default_currency} onValueChange={(v) => setTmplDraft({...tmplDraft, default_currency: v})}>
                    <SelectTrigger data-testid="tmpl-currency"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="KES">KES</SelectItem><SelectItem value="USD">USD</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1"><Label>Default vendor</Label><Input value={tmplDraft.vendor || ""} onChange={(e) => setTmplDraft({...tmplDraft, vendor: e.target.value})} data-testid="tmpl-vendor" /></div>
              <label className="flex items-center gap-2"><input type="checkbox" checked={!!tmplDraft.auto_generate} onChange={(e) => setTmplDraft({...tmplDraft, auto_generate: e.target.checked})} data-testid="tmpl-auto" /><span className="text-sm">Auto-generate on day {tmplDraft.day_of_month} each month</span></label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setTmplDraft(null)}>Cancel</Button>
            <Button onClick={saveTemplate} className="bg-[#1B4332] hover:bg-[#2D6A4F]" data-testid="tmpl-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ label, value, sub, color, testId }) {
  return (
    <Card className="border-[#E8E8E6]" data-testid={testId}>
      <CardContent className="py-4">
        <p className="text-[10px] uppercase tracking-wider text-[#7A8A84] font-semibold">{label}</p>
        <p className="text-2xl font-bold tabular-nums mt-1" style={{ color }}>{value}</p>
        {sub && <p className="text-[10px] text-[#7A8A84] mt-1">{sub}</p>}
      </CardContent>
    </Card>
  );
}

function TabButton({ current, setTab, k, label }) {
  const on = current === k;
  return (
    <button onClick={() => setTab(k)} data-testid={`tab-${k}`}
      className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
        on ? "border-[#B5392F] text-[#B5392F]" : "border-transparent text-[#7A8A84] hover:text-[#1C1F1E]"
      }`}>{label}</button>
  );
}
